/**
 * Shared shape of a crossword question (one theme row in `questions_and_answers`).
 * Used by the admin editor, the admin grid preview and the game, so all three
 * agree on which questions are usable and therefore generate the same grid.
 *
 *   text  → { type, question, answer }
 *   image → { type, answer, media, heading?, sentence? }
 *   video → { type, answer, media, heading?, sentence? }
 *
 * Rows saved before question types existed have no `type` and are treated as text.
 */

import { ensureThemeJsonArray } from "./themeAssets";

export const QUESTION_TYPES = ["text", "image", "video"];

export const QUESTION_MEDIA_RULES = {
  image: {
    extensions: ["png", "jpg", "jpeg", "webp"],
    accept: ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp",
    maxMb: 5,
  },
  video: {
    extensions: ["mp4"],
    accept: ".mp4,video/mp4",
    maxMb: 20,
  },
};

export function normalizeCrosswordQuestion(raw) {
  const p = raw && typeof raw === "object" ? raw : {};
  const type = QUESTION_TYPES.includes(p.type) ? p.type : "text";
  return {
    type,
    question: String(p.question ?? "").trim(),
    answer: String(p.answer ?? "").trim(),
    media: String(p.media ?? "").trim(),
    heading: String(p.heading ?? "").trim(),
    sentence: String(p.sentence ?? "").trim(),
  };
}

export function isUsableCrosswordQuestion(q) {
  if (!q.answer) return false;
  return q.type === "text" ? Boolean(q.question) : Boolean(q.media);
}

/** Plain-text label for a question (clue lists, reports, admin preview). */
export function crosswordClueLabel(q) {
  if (q.type === "text") return q.question;
  return q.heading || q.sentence || (q.type === "image" ? "Image clue" : "Video clue");
}

export function questionMediaUrl(media) {
  return media ? `${process.env.REACT_APP_S3_PATH || ""}${media}` : "";
}

/** Normalized, usable questions from a theme's `questions_and_answers` value. */
export function getUsableCrosswordQuestions(value) {
  return ensureThemeJsonArray(value)
    .map(normalizeCrosswordQuestion)
    .filter(isUsableCrosswordQuestion);
}

/** Input words for generateLayout(); media fields ride along on each placed word. */
export function toLayoutWords(questions) {
  return questions.map((q) => ({
    answer: q.answer.toLowerCase(),
    clue: crosswordClueLabel(q),
    orientation: "none",
    type: q.type,
    media: q.media,
    heading: q.heading,
    sentence: q.sentence,
  }));
}
